/**
 * Spec 056 — Reservas Online (research D15).
 *
 * Decide, ANTES de montar cualquier router, si la página corre como sitio
 * público de reservas (BookingApp) o como la app autenticada de Prikly.
 *
 * - Ruta `/reservar/*` o `/cita/*` ⇒ reservas, en CUALQUIER host (URL de
 *   respaldo). Siempre activo, con o sin flag.
 * - Por HOST solo si `VITE_BOOKING_ONLINE_ENABLED=true`:
 *   - subdominio de UN nivel del dominio base (`{slug}.prikly.io`) que no sea
 *     host de la app, `www` ni el dominio raíz ⇒ reservas;
 *   - host que no termina en el dominio base ni es host de la app
 *     (dominio propio del negocio) ⇒ reservas.
 * - Nunca por host: localhost (y `*.localhost`), IPs, `*.cloud.elastika.pe`,
 *   `*.vercel.app`.
 *
 * OJO: `/reservas/*` es la agenda interna de la app y NO debe capturarse; por
 * eso se compara con el prefijo exacto incluyendo la barra final.
 */

export const DEFAULT_BOOKING_APP_HOSTS: readonly string[] = [
  'app.prikly.io',
  'app-qa.prikly.io',
  'app-uat.prikly.io',
  'localhost',
];

/** Prefijos de ruta que siempre pertenecen al sitio público de reservas. */
export const BOOKING_PATH_PREFIXES: readonly string[] = ['/reservar/', '/cita/'];

const IPV4_RE = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;

/** Normaliza un hostname: minúsculas, sin puerto, sin corchetes IPv6 ni punto final. */
export function normalizeHostname(hostname: string): string {
  let host = (hostname || '').trim().toLowerCase();
  if (host.startsWith('[')) {
    const end = host.indexOf(']');
    return end > 0 ? host.slice(1, end) : host.slice(1);
  }
  // Puerto (solo si no es IPv6 sin corchetes: IPv6 tiene varios ':').
  const colonCount = (host.match(/:/g) || []).length;
  if (colonCount === 1) host = host.slice(0, host.indexOf(':'));
  if (host.endsWith('.')) host = host.slice(0, -1);
  return host;
}

/** true si el hostname es una IP (v4 o v6). */
export function isIpAddress(hostname: string): boolean {
  const host = normalizeHostname(hostname);
  const v4 = IPV4_RE.exec(host);
  if (v4) return v4.slice(1).every((part) => Number(part) <= 255);
  // IPv6: al menos dos ':' y solo hex / ':' / '.' (IPv4 embebida).
  return host.includes(':') && /^[0-9a-f:.]+$/.test(host);
}

/**
 * Parsea `VITE_BOOKING_APP_HOSTS` (lista separada por comas). Vacío o
 * indefinido ⇒ lista por defecto.
 */
export function parseAppHosts(raw: string | undefined | null): string[] {
  const list = (raw ?? '')
    .split(',')
    .map((h) => normalizeHostname(h))
    .filter((h) => h.length > 0);
  return list.length > 0 ? list : [...DEFAULT_BOOKING_APP_HOSTS];
}

/** true si el hostname corresponde a la app autenticada de Prikly. */
export function isAppHost(hostname: string, appHosts: readonly string[]): boolean {
  const host = normalizeHostname(hostname);
  if (!host) return true; // sin host (entornos raros/tests): no secuestrar la app
  if (host === 'localhost' || host === '127.0.0.1' || isIpAddress(host)) return true;
  return appHosts.some((h) => normalizeHostname(h) === host);
}

/** true si la ruta pertenece al sitio público de reservas. */
export function isBookingPath(pathname: string): boolean {
  const path = pathname || '/';
  return BOOKING_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
}

/** Dominio base por defecto de los subdominios de negocio. */
export const DEFAULT_BOOKING_BASE_DOMAIN = 'prikly.io';

/** Sufijos de infraestructura que NUNCA se tratan como host de reservas. */
export const NEVER_BOOKING_HOST_SUFFIXES: readonly string[] = [
  '.cloud.elastika.pe',
  '.vercel.app',
  '.localhost',
];

export interface BookingHostOptions {
  /** Hosts de la app autenticada (`VITE_BOOKING_APP_HOSTS`). */
  appHosts: readonly string[];
  /** `VITE_BOOKING_ONLINE_ENABLED`: sin él, nunca se decide por host. */
  enabled: boolean;
  /** `VITE_BOOKING_BASE_DOMAIN` (default `prikly.io`). */
  baseDomain?: string;
}

/** true si el host jamás puede ser de reservas (infra, IP, localhost). */
export function isNeverBookingHost(hostname: string): boolean {
  const host = normalizeHostname(hostname);
  if (!host) return true;
  if (host === 'localhost' || isIpAddress(host)) return true;
  return NEVER_BOOKING_HOST_SUFFIXES.some(
    (suffix) => host.endsWith(suffix) || host === suffix.slice(1),
  );
}

/**
 * true si el HOST (sin mirar la ruta) es de un negocio. Requiere el flag.
 */
export function isBookingBusinessHost(hostname: string, options: BookingHostOptions): boolean {
  if (!options.enabled) return false;
  const host = normalizeHostname(hostname);
  if (isNeverBookingHost(host)) return false;
  if (isAppHost(host, options.appHosts)) return false;

  const base = normalizeHostname(options.baseDomain || '') || DEFAULT_BOOKING_BASE_DOMAIN;
  if (host === base || host === `www.${base}`) return false;
  if (host.endsWith(`.${base}`)) {
    const label = host.slice(0, -(base.length + 1));
    // Solo subdominios de un nivel: `carppone.prikly.io`, no `a.b.prikly.io`.
    return label.length > 0 && !label.includes('.');
  }
  // Dominio propio del negocio.
  return true;
}

/**
 * Función pura principal: ¿se debe montar BookingApp?
 */
export function isBookingHost(
  hostname: string,
  pathname: string,
  options: BookingHostOptions,
): boolean {
  if (isBookingPath(pathname)) return true;
  return isBookingBusinessHost(hostname, options);
}

/** Lista de hosts de la app desde `VITE_BOOKING_APP_HOSTS` (o la default). */
export function readAppHostsFromEnv(): string[] {
  const raw = import.meta.env?.VITE_BOOKING_APP_HOSTS as string | undefined;
  return parseAppHosts(raw);
}

/** Opciones completas desde las variables `VITE_BOOKING_*`. */
export function readBookingHostOptionsFromEnv(): BookingHostOptions {
  const env = import.meta.env ?? ({} as ImportMetaEnv);
  return {
    appHosts: readAppHostsFromEnv(),
    enabled: String(env.VITE_BOOKING_ONLINE_ENABLED ?? '').trim().toLowerCase() === 'true',
    baseDomain:
      normalizeHostname(String(env.VITE_BOOKING_BASE_DOMAIN ?? '')) || DEFAULT_BOOKING_BASE_DOMAIN,
  };
}
